/**
 * AddEmployeeScreen.js
 * Single dedicated screen for adding employees.
 * Navigate to it from anywhere in the app:
 *
 *   navigation.navigate('AddEmployeeScreen', {
 *     companyId: '1',               // optional
 *     onSuccess: (employee) => {},  // optional callback
 *     source: 'hr',                 // optional tag
 *   });
 */

import React, { useState, useCallback, useRef } from 'react';
import {
  View, Text, ScrollView, TextInput, TouchableOpacity,
  StyleSheet, Alert, ActivityIndicator, Image, Platform,
  KeyboardAvoidingView, StatusBar,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { getCallback, clearCallback } from '../../../utils/navigationCallbacks';
import * as ImagePicker from 'expo-image-picker';

const API_BASE = 'https://api.jobeng.ai';
const API_KEY  = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
const PRIMARY  = '#0e4e96';
const SUCCESS  = '#10b981';
const DANGER   = '#EF4444';

const DEPARTMENTS = ['Engineering','Sales','Marketing','HR','Finance','Operations','Customer Service','IT','Legal','Research','Support','Product'];
const EMP_TYPES   = ['Full-time','Part-time','Contract','Intern','Temporary'];
const LOCATIONS   = ['Office','Remote','Hybrid'];
const STEPS       = ['Basic Info','Employment','Personal','Payroll'];

// ── API ────────────────────────────────────────────────────────────────────────
const getHeaders = async () => {
  const token = await AsyncStorage.getItem('auth_token').catch(() => null)
             || await AsyncStorage.getItem('token').catch(() => null);
  return {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    'x-api-key': API_KEY,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

// ── Sub-components ─────────────────────────────────────────────────────────────
const Field = React.memo(({ label, value, onChange, placeholder, kb, multiline, error, required, note }) => (
  <View style={{ marginBottom: 16 }}>
    <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 6 }}>
      <Text style={s.label}>{label}{required && <Text style={{ color: DANGER }}> *</Text>}</Text>
      {note ? <Text style={{ fontSize: 11, color: '#9CA3AF' }}>{note}</Text> : null}
    </View>
    <TextInput
      style={[s.input, multiline && { height: 80, textAlignVertical: 'top', paddingTop: 12 }, error && { borderColor: DANGER }]}
      value={String(value ?? '')}
      onChangeText={onChange}
      placeholder={placeholder}
      placeholderTextColor="#D1D5DB"
      keyboardType={kb || 'default'}
      autoCapitalize={kb === 'email-address' ? 'none' : 'sentences'}
      multiline={multiline}
    />
    {error ? <Text style={{ fontSize: 11, color: DANGER, marginTop: 4 }}>{error}</Text> : null}
  </View>
));

const ChipRow = ({ label, options, value, onChange, color = PRIMARY }) => (
  <View style={{ marginBottom: 16 }}>
    <Text style={[s.label, { marginBottom: 8 }]}>{label}</Text>
    <ScrollView horizontal showsHorizontalScrollIndicator={false}>
      <View style={{ flexDirection: 'row', gap: 8, paddingRight: 16 }}>
        {options.map(opt => {
          const active = value === opt;
          return (
            <TouchableOpacity key={opt} onPress={() => onChange(opt)}
              style={{ paddingHorizontal: 14, paddingVertical: 8, borderRadius: 20, borderWidth: 1.5,
                backgroundColor: active ? color : '#fff', borderColor: active ? color : '#E5E7EB' }}>
              <Text style={{ fontSize: 13, fontWeight: '600', color: active ? '#fff' : '#6B7280' }}>{opt}</Text>
            </TouchableOpacity>
          );
        })}
      </View>
    </ScrollView>
  </View>
);

const SectionHeader = ({ title, icon }) => (
  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 24, marginBottom: 16,
    paddingBottom: 10, borderBottomWidth: 1, borderBottomColor: '#E5E7EB' }}>
    <View style={{ width: 32, height: 32, borderRadius: 8, backgroundColor: PRIMARY + '15',
      alignItems: 'center', justifyContent: 'center' }}>
      <MaterialCommunityIcons name={icon} size={18} color={PRIMARY} />
    </View>
    <Text style={{ fontSize: 15, fontWeight: '800', color: '#111827' }}>{title}</Text>
  </View>
);

// ── EMPTY FORM ─────────────────────────────────────────────────────────────────
const EMPTY = {
  firstName: '', lastName: '', email: '', phone: '',
  department: 'Engineering', position: '', salary: '',
  startDate: new Date().toISOString().split('T')[0],
  employeeType: 'Full-time', location: 'Office',
  manager: '', address: '', city: '', postalCode: '',
  nationalId: '', niNumber: '', taxCode: '1257L',
  emergencyContact: '', emergencyPhone: '',
  skills: '', notes: '', avatar: null, avatarBase64: null,
};

// ── MAIN SCREEN ────────────────────────────────────────────────────────────────
export default function AddEmployeeScreen({ navigation, route }) {
  const { companyId, existingCount = 0, onSave: onSaveProp, onSuccess, callbackKey } = route?.params || {};
  // Resolve callback — either passed directly (legacy) or via registry key (preferred, avoids serialization warning)
  const onSave = onSaveProp || (callbackKey ? getCallback(callbackKey) : null);

  const [form, setForm]     = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [step, setStep]     = useState(0);
  // Ref to avoid stale closure — always holds latest base64 value
  const avatarBase64Ref = useRef(null);
  const avatarUriRef    = useRef(null);

  const set = useCallback((field) => (val) => {
    setForm(p => ({ ...p, [field]: val }));
    if (field === 'avatarBase64') avatarBase64Ref.current = val;
    if (field === 'avatar')       avatarUriRef.current    = val;
  }, []);

  // ── Photo picker ─────────────────────────────────────────────────────────────
  const pickPhoto = () => {
    Alert.alert('Employee Photo', 'Choose source', [
      {
        text: 'Camera', onPress: async () => {
          const { status } = await ImagePicker.requestCameraPermissionsAsync();
          if (status !== 'granted') { Alert.alert('Permission Required', 'Allow camera access.'); return; }
          const r = await ImagePicker.launchCameraAsync({ allowsEditing: true, aspect: [1, 1], quality: 0.5, base64: true });
          if (!r.canceled && r.assets?.[0]) {
            set('avatar')(r.assets[0].uri);
            set('avatarBase64')(r.assets[0].base64 || null);
          }
        },
      },
      {
        text: 'Library', onPress: async () => {
          const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
          if (status !== 'granted') { Alert.alert('Permission Required', 'Allow photo library access.'); return; }
          const r = await ImagePicker.launchImageLibraryAsync({ allowsEditing: true, aspect: [1, 1], quality: 0.5, base64: true, mediaTypes: ImagePicker.MediaTypeOptions.Images });
          if (!r.canceled && r.assets?.[0]) {
            set('avatar')(r.assets[0].uri);
            set('avatarBase64')(r.assets[0].base64 || null);
          }
        },
      },
      { text: 'Cancel', style: 'cancel' },
    ]);
  };

  // ── Validate ─────────────────────────────────────────────────────────────────
  const validate = () => {
    const e = {};
    if (!form.firstName.trim()) e.firstName = 'Required';
    if (!form.lastName.trim())  e.lastName  = 'Required';
    if (!form.email.trim())     e.email     = 'Required';
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Invalid email';
    if (!form.position.trim())  e.position  = 'Required';
    if (form.salary && isNaN(parseInt(form.salary))) e.salary = 'Must be a number';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // ── Save ──────────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    console.log('[AddEmployee] handleSave called, form:', JSON.stringify({
      firstName: form.firstName, lastName: form.lastName,
      email: form.email, position: form.position, step,
    }));
    if (!validate()) {
      const _e = {};
      if (!form.firstName?.trim()) _e.firstName = 'Required';
      if (!form.email?.trim()) _e.email = 'Required';
      if (!form.position?.trim()) _e.position = 'Required';
      console.log('[AddEmployee] Validation failed:', JSON.stringify(_e), 'form:', JSON.stringify({ firstName: form.firstName, email: form.email, position: form.position }));
      Alert.alert('Required Fields Missing', 'Please fill in: Name, Email, and Job Title (Step 1)');
      setStep(0);
      return;
    }
    console.log('[AddEmployee] Validation passed, saving...');
    setSaving(true);
    try {
      const localId  = `emp_${Date.now()}`;
      const fullName = `${form.firstName.trim()} ${form.lastName.trim()}`.trim();
      const skills   = (form.skills || '').split(',').map(s => s.trim()).filter(Boolean);
      const cid      = companyId || await AsyncStorage.getItem('company_id').catch(() => null) || '0';

      // Local employee record (camelCase for app)
      const localEmp = {
        id: localId, name: fullName,
        firstName: form.firstName.trim(), lastName: form.lastName.trim(),
        email: form.email.trim(), phone: form.phone || '',
        department: form.department, position: form.position.trim(),
        salary: parseInt(form.salary) || 0, status: 'Active',
        startDate: form.startDate, employeeType: form.employeeType,
        location: form.location, manager: form.manager || '',
        address: [form.address, form.city, form.postalCode].filter(Boolean).join(', '),
        nationalId: form.nationalId || '', niNumber: form.niNumber || '',
        taxCode: form.taxCode || '1257L',
        emergencyContact: form.emergencyContact || '', emergencyPhone: form.emergencyPhone || '',
        skills, notes: form.notes || '',
        avatar: form.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(fullName)}&background=0e4e96&color=fff&size=128`,
        performanceRating: 3, attendanceRate: 100, leaveBalance: 25, warningCount: 0,
        createdAt: new Date().toISOString(),
      };

      // Server payload (snake_case)
      const serverPayload = {
        first_name: (form.firstName || '').trim(), last_name: (form.lastName || '').trim(),
        email: form.email.trim(), phone: form.phone || null,
        department: form.department || null, position: (form.position || '').trim() || null,
        hire_date: form.startDate || null, status: 'active',
        employment_type: (form.employeeType || 'full_time').toLowerCase().replace(/[- ]/g, '_'),
        salary: form.salary ? parseInt(form.salary) : null,
        notes: form.notes || null, address: form.address || null,
        national_id: form.nationalId || null, ni_number: form.niNumber || null,
        tax_code: form.taxCode || '1257L',
        emergency_contact_name: form.emergencyContact || null,
        emergency_contact_phone: form.emergencyPhone || null,
        company_id: parseInt(cid) || 0, manager: form.manager || null,
        profile_image: form.avatar || null,
      };

      // 1. Write to ALL shared caches immediately
      const cacheKeys = [`hr_employees_${cid}`, 'hr_employees', 'mgmt_emp_cache'];
      for (const key of cacheKeys) {
        try {
          const raw = await AsyncStorage.getItem(key);
          const arr = raw ? JSON.parse(raw) : [];
          if (Array.isArray(arr)) await AsyncStorage.setItem(key, JSON.stringify([localEmp, ...arr]));
        } catch (_) {}
      }

      // 2. POST to server
      let serverId = null;
      try {
        console.log('[AddEmployee] POSTing to server:', JSON.stringify(serverPayload));
        const res = await fetch(`${API_BASE}/api/employees`, {
          method: 'POST', headers: await getHeaders(), body: JSON.stringify(serverPayload),
        });
        console.log('[AddEmployee] Server response status:', res.status);
        const json = await res.json().catch(() => ({}));
        console.log('[AddEmployee] Server response body:', JSON.stringify(json).slice(0, 300));
        if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
        serverId = json?.data?.id ?? json?.id ?? null;
        console.log('[AddEmployee] Server ID assigned:', serverId);

        if (serverId && String(serverId) !== localId) {
          const updatedEmp = { ...localEmp, id: serverId, serverId };
          for (const key of cacheKeys) {
            try {
              const raw = await AsyncStorage.getItem(key);
              const arr = raw ? JSON.parse(raw) : [];
              if (Array.isArray(arr)) {
                await AsyncStorage.setItem(key, JSON.stringify(arr.map(e => e.id === localId ? updatedEmp : e)));
              }
            } catch (_) {}
          }

          // Upload photo to server if we have base64 data
          const b64 = avatarBase64Ref.current || form.avatarBase64;
          if (serverId && b64) {
            try {
              const photoRes = await fetch(`${API_BASE}/api/employees/${serverId}/photo`, {
                method: 'POST',
                headers: await getHeaders(),
                body: JSON.stringify({
                  image_data: `data:image/jpeg;base64,${b64}`,
                  image_type: 'jpeg',
                }),
              });
              const photoJson = await photoRes.json().catch(() => ({}));
              if (photoJson.success && photoJson.data?.profile_image) {
                // Update local records with the server photo URL
                const publicUrl = `${API_BASE}${photoJson.data.profile_image}`;
                console.log('[AddEmployee] Photo uploaded:', publicUrl);
                for (const key of cacheKeys) {
                  try {
                    const raw = await AsyncStorage.getItem(key);
                    const arr = raw ? JSON.parse(raw) : [];
                    if (Array.isArray(arr)) {
                      await AsyncStorage.setItem(key, JSON.stringify(
                        arr.map(e => (e.id === serverId || e.id === localId) ? { ...e, avatar: publicUrl, profile_image: publicUrl } : e)
                      ));
                    }
                  } catch (_) {}
                }
              }
            } catch (photoErr) {
              console.warn('[AddEmployee] Photo upload failed (employee saved without photo):', photoErr.message);
            }
          } else if (serverId && avatarUriRef.current && !avatarUriRef.current.startsWith('https://ui-avatars')) {
            // Fallback: send local URI as URL string
            fetch(`${API_BASE}/api/employees/${serverId}`, {
              method: 'PUT', headers: await getHeaders(),
              body: JSON.stringify({ profile_image: avatarUriRef.current }),
            }).catch(() => {});
          }
          if (!serverId && b64) {
            console.warn('[AddEmployee] No server ID — photo will upload after employee syncs');
          }
        }
      } catch (apiErr) {
        console.warn('[AddEmployeeScreen] Server POST failed (saved locally):', apiErr.message);
      }

      // 3. Payroll sync
      try {
        const syncRaw = await AsyncStorage.getItem('payroll_employee_sync');
        const syncData = syncRaw ? JSON.parse(syncRaw) : {};
        const finalId = serverId || localId;
        syncData[finalId] = { ...localEmp, id: finalId, payFrequency: 'monthly', paymentDate: 28, taxCode: form.taxCode || '1257L' };
        await AsyncStorage.setItem('payroll_employee_sync', JSON.stringify(syncData));
      } catch (_) {}

      const finalEmp = { ...localEmp, id: serverId || localId, serverId };

      try {
        if (typeof onSave === 'function')    await onSave(finalEmp);
        if (typeof onSuccess === 'function') await onSuccess(finalEmp);
        if (callbackKey) clearCallback(callbackKey); // clean up registry
      } catch (cbErr) {
        console.warn('[AddEmployee] onSave callback error (non-blocking):', cbErr.message);
        if (callbackKey) clearCallback(callbackKey);
      }

      Alert.alert('✅ Employee Added', `${fullName} has been added successfully.`, [
        { text: 'View Profile', onPress: () => navigation.replace('EmployeeDetailsScreen', { employee: finalEmp, employeeId: finalEmp.id }) },
        { text: 'Add Another',  onPress: () => { setForm(EMPTY); setErrors({}); setStep(0); } },
        { text: 'Done',         onPress: () => navigation.goBack() },
      ]);
    } catch (err) {
      console.error('[AddEmployeeScreen]', err);
      Alert.alert('Error', err.message || 'Failed to add employee');
    } finally {
      setSaving(false);
    }
  };

  // ── Step renders ─────────────────────────────────────────────────────────────
  const _nameForAvatar = (`${form.firstName || ''} ${form.lastName || ''}`).trim() || 'New Employee';
  const avatarUri = (form.avatar && form.avatar.length > 0)
    ? form.avatar
    : `https://ui-avatars.com/api/?name=${encodeURIComponent(_nameForAvatar)}&background=0e4e96&color=fff&size=200`;

  const step0 = () => (
    <>
      <TouchableOpacity onPress={pickPhoto} style={{ alignItems: 'center', marginBottom: 24 }}>
        <View style={{ position: 'relative' }}>
          <Image
                source={avatarUri ? { uri: avatarUri } : { uri: 'https://ui-avatars.com/api/?name=New&background=0e4e96&color=fff&size=200' }}
                style={{ width: 90, height: 90, borderRadius: 45, borderWidth: 3, borderColor: PRIMARY }}
              />
          <View style={{ position: 'absolute', bottom: 0, right: 0, width: 28, height: 28, borderRadius: 14,
            backgroundColor: PRIMARY, borderWidth: 2, borderColor: '#fff', alignItems: 'center', justifyContent: 'center' }}>
            <MaterialCommunityIcons name="camera" size={14} color="#fff" />
          </View>
        </View>
        <Text style={{ fontSize: 12, color: PRIMARY, fontWeight: '600', marginTop: 8 }}>{form.avatar ? 'Change Photo' : 'Add Photo'}</Text>
      </TouchableOpacity>
      <View style={{ flexDirection: 'row', gap: 12 }}>
        <View style={{ flex: 1 }}><Field label="First Name" value={form.firstName} onChange={set('firstName')} placeholder="Jane" required error={errors.firstName} /></View>
        <View style={{ flex: 1 }}><Field label="Last Name"  value={form.lastName}  onChange={set('lastName')}  placeholder="Smith" required error={errors.lastName} /></View>
      </View>
      <Field label="Work Email"          value={form.email}    onChange={set('email')}    placeholder="jane@company.com" kb="email-address" required error={errors.email} />
      <Field label="Phone"               value={form.phone}    onChange={set('phone')}    placeholder="+44 7xxx xxxxxx" kb="phone-pad" />
      <Field label="Job Title / Position" value={form.position} onChange={set('position')} placeholder="Senior Developer" required error={errors.position} />
      <ChipRow label="Department" options={DEPARTMENTS} value={form.department} onChange={set('department')} />
    </>
  );

  const step1 = () => (
    <>
      <SectionHeader title="Employment Details" icon="briefcase-outline" />
      <ChipRow label="Employment Type" options={EMP_TYPES} value={form.employeeType} onChange={set('employeeType')} color="#8b5cf6" />
      <ChipRow label="Work Location"   options={LOCATIONS}  value={form.location}     onChange={set('location')}     color="#06b6d4" />
      <Field label="Annual Salary (£)" value={form.salary}    onChange={set('salary')}    placeholder="50000" kb="numeric" error={errors.salary} note="Gross annual" />
      <Field label="Start Date"        value={form.startDate} onChange={set('startDate')} placeholder="YYYY-MM-DD" note="YYYY-MM-DD" />
      <Field label="Reporting Manager" value={form.manager}   onChange={set('manager')}   placeholder="Manager name" />
      <Field label="Notes"             value={form.notes}     onChange={set('notes')}     placeholder="Any additional notes…" multiline />
    </>
  );

  const step2 = () => (
    <>
      <SectionHeader title="Personal Information" icon="account-outline" />
      <Field label="Address Line" value={form.address}    onChange={set('address')}    placeholder="123 Main Street" />
      <View style={{ flexDirection: 'row', gap: 12 }}>
        <View style={{ flex: 2 }}><Field label="City"      value={form.city}       onChange={set('city')}       placeholder="London" /></View>
        <View style={{ flex: 1 }}><Field label="Post Code" value={form.postalCode}  onChange={set('postalCode')} placeholder="SW1A 1AA" /></View>
      </View>
      <Field label="National ID / Passport" value={form.nationalId} onChange={set('nationalId')} placeholder="National ID number" />
      <SectionHeader title="Emergency Contact" icon="phone-alert-outline" />
      <Field label="Name"  value={form.emergencyContact} onChange={set('emergencyContact')} placeholder="John Smith" />
      <Field label="Phone" value={form.emergencyPhone}   onChange={set('emergencyPhone')}   placeholder="+44 7xxx xxxxxx" kb="phone-pad" />
      <SectionHeader title="Skills" icon="star-outline" />
      <Field label="Skills (comma-separated)" value={form.skills} onChange={set('skills')} placeholder="JavaScript, React Native, Python" multiline note="Separate with commas" />
    </>
  );

  const step3 = () => (
    <>
      <SectionHeader title="UK Payroll & Tax" icon="cash-multiple" />
      <View style={{ backgroundColor: '#EFF6FF', borderRadius: 12, padding: 14, marginBottom: 16 }}>
        <Text style={{ fontSize: 12, color: '#3B82F6', fontWeight: '600', lineHeight: 18 }}>
          These are optional. Default tax code 1257L applies if blank. You can update later from the Payroll screen.
        </Text>
      </View>
      <Field label="NI Number" value={form.niNumber} onChange={set('niNumber')} placeholder="AB 12 34 56 C" note="National Insurance" />
      <Field label="Tax Code"  value={form.taxCode}  onChange={set('taxCode')}  placeholder="1257L" note="Default: 1257L" />
      <SectionHeader title="Review & Confirm" icon="check-circle-outline" />
      <View style={{ backgroundColor: '#F0FDF4', borderRadius: 12, padding: 16 }}>
        {[
          ['Name',       `${form.firstName} ${form.lastName}`.trim() || '—'],
          ['Email',      form.email || '—'],
          ['Department', form.department],
          ['Position',   form.position || '—'],
          ['Salary',     form.salary ? `£${parseInt(form.salary).toLocaleString()}/yr` : '—'],
          ['Type',       form.employeeType],
          ['Location',   form.location],
          ['Start',      form.startDate || '—'],
          ['NI Number',  form.niNumber || 'Not set'],
          ['Tax Code',   form.taxCode  || '1257L'],
        ].map(([l, v]) => (
          <View key={l} style={{ flexDirection: 'row', paddingVertical: 6, borderBottomWidth: 1, borderBottomColor: '#D1FAE5' }}>
            <Text style={{ width: 100, fontSize: 12, color: '#6B7280', fontWeight: '600' }}>{l}</Text>
            <Text style={{ flex: 1, fontSize: 12, color: '#111827', fontWeight: '700' }}>{v}</Text>
          </View>
        ))}
      </View>
    </>
  );

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: '#F8FAFC' }} edges={['top','left','right']}>
      <StatusBar barStyle="dark-content" />

      {/* Header */}
      <View style={s.header}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={{ padding: 6 }}>
          <MaterialCommunityIcons name="arrow-left" size={24} color="#374151" />
        </TouchableOpacity>
        <View style={{ flex: 1, marginHorizontal: 12 }}>
          <Text style={s.headerTitle}>Add New Employee</Text>
          <Text style={{ fontSize: 12, color: '#9CA3AF' }}>Step {step + 1} of {STEPS.length} — {STEPS[step]}</Text>
        </View>
        <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}>
          {step < STEPS.length - 1 && (
            <TouchableOpacity onPress={handleSave} disabled={saving}
              style={{ borderRadius: 10, paddingHorizontal: 12, paddingVertical: 8, borderWidth: 1.5, borderColor: SUCCESS }}>
              <Text style={{ color: SUCCESS, fontWeight: '700', fontSize: 11 }}>Save Now</Text>
            </TouchableOpacity>
          )}
          <TouchableOpacity
            onPress={step === STEPS.length - 1 ? handleSave : () => setStep(p => p + 1)}
            disabled={saving}
            style={{ backgroundColor: step === STEPS.length - 1 ? SUCCESS : PRIMARY, borderRadius: 10, paddingHorizontal: 16, paddingVertical: 8 }}>
            {saving
              ? <ActivityIndicator size="small" color="#fff" />
              : <Text style={{ color: '#fff', fontWeight: '700', fontSize: 13 }}>{step === STEPS.length - 1 ? 'Save' : 'Next →'}</Text>}
          </TouchableOpacity>
        </View>
      </View>

      {/* Step indicator */}
      <View style={{ flexDirection: 'row', backgroundColor: '#fff', paddingVertical: 12, paddingHorizontal: 20,
        borderBottomWidth: 1, borderBottomColor: '#E5E7EB' }}>
        {STEPS.map((label, i) => (
          <TouchableOpacity key={label} onPress={() => setStep(i)} style={{ flex: 1, alignItems: 'center' }}>
            <View style={{ width: 28, height: 28, borderRadius: 14, alignItems: 'center', justifyContent: 'center',
              backgroundColor: i === step ? PRIMARY : i < step ? SUCCESS : '#E5E7EB' }}>
              {i < step
                ? <MaterialCommunityIcons name="check" size={16} color="#fff" />
                : <Text style={{ fontSize: 12, fontWeight: '700', color: i === step ? '#fff' : '#9CA3AF' }}>{i + 1}</Text>}
            </View>
            <Text style={{ fontSize: 9, fontWeight: '700', color: i === step ? PRIMARY : '#9CA3AF', marginTop: 4, textAlign: 'center' }}>{label}</Text>
          </TouchableOpacity>
        ))}
      </View>

      <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
        <ScrollView contentContainerStyle={{ padding: 20, paddingBottom: 60 }} keyboardShouldPersistTaps="handled" showsVerticalScrollIndicator={false}>
          {step === 0 && step0()}
          {step === 1 && step1()}
          {step === 2 && step2()}
          {step === 3 && step3()}

          {/* Bottom nav buttons */}
          <View style={{ flexDirection: 'row', gap: 12, marginTop: 24 }}>
            {step > 0 && (
              <TouchableOpacity onPress={() => setStep(p => p - 1)} style={s.backBtn}>
                <MaterialCommunityIcons name="arrow-left" size={16} color="#6B7280" />
                <Text style={{ fontSize: 14, fontWeight: '700', color: '#6B7280' }}>Back</Text>
              </TouchableOpacity>
            )}
            <TouchableOpacity
              onPress={step === STEPS.length - 1 ? handleSave : () => setStep(p => p + 1)}
              disabled={saving}
              style={[s.nextBtn, { flex: step > 0 ? 2 : 1, backgroundColor: step === STEPS.length - 1 ? SUCCESS : PRIMARY }]}>
              {saving
                ? <ActivityIndicator size="small" color="#fff" />
                : <Text style={{ fontSize: 14, fontWeight: '700', color: '#fff' }}>
                    <Text>{step === STEPS.length - 1 ? 'Save Employee' : `Next: ${STEPS[step + 1]}`}</Text>
                  </Text>}
            </TouchableOpacity>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

const s = StyleSheet.create({
  header:      { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingVertical: 12, backgroundColor: '#fff', borderBottomWidth: 1, borderBottomColor: '#E5E7EB' },
  headerTitle: { fontSize: 17, fontWeight: '800', color: '#111827' },
  label:       { fontSize: 12, fontWeight: '700', color: '#374151', textTransform: 'uppercase', letterSpacing: 0.4 },
  input:       { backgroundColor: '#fff', borderWidth: 1.5, borderColor: '#E5E7EB', borderRadius: 12, paddingHorizontal: 14, paddingVertical: 12, fontSize: 14, color: '#111827' },
  backBtn:     { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, backgroundColor: '#F3F4F6', borderRadius: 14, paddingVertical: 14 },
  nextBtn:     { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, borderRadius: 14, paddingVertical: 14 },
});